"use client";

import { useEffect } from "react";
import { Link, useRouter } from "@/i18n/navigation";
import { AuroraBackground } from "@/components/aurora-background";
import { Logo } from "@/components/brand/logo";
import { useAuthStore } from "@/lib/stores/auth-store";

/**
 * Shell for the login, register and password pages.
 *
 * Also the guard that sends an already-signed-in visitor to their dashboard.
 * Without it /login and /register rendered their forms to someone who was
 * already authenticated, which reads as "your session expired" and invites them
 * to sign in again for no reason. The dashboard layout has always had the
 * mirror of this guard; this side was simply missing.
 */
export default function AuthLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const { isAuthenticated, isLoading, loadUser } = useAuthStore();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    // Only redirect once the profile request has settled. Acting while
    // isLoading is true would bounce every visitor on a cold load, including
    // the signed-out ones this page exists for.
    if (!isLoading && isAuthenticated) {
      router.replace("/overview");
    }
  }, [isLoading, isAuthenticated, router]);

  return (
    <div className="relative flex min-h-screen items-center justify-center">
      <AuroraBackground />
      <div className="w-full max-w-md px-6 py-12">
        <Link
          href="/"
          className="mb-8 flex justify-center text-2xl"
        >
          <Logo tileClassName="size-9" />
        </Link>
        {children}
      </div>
    </div>
  );
}
